import React, { useMemo, useState } from "react"

import { FormProvider, useForm } from "react-hook-form"

import BulkApplyForm from "./BulkApplyForm"
import SalonSettingsTable from "./SalonSettingsTable"
import styles from "./SmsSendingStartSettingsTab.module.scss"

import { useSouhlAdminMutation } from "@/provider/apollo/Apollo"
import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/souhl_admin/gql/__generated__"
import { CompanySmsSendingRuleAutoClaimBase } from "@/souhl_admin/gql/__generated__/graphql"
import {
	SmsSendingStartSettingsForm,
	smsSendingStartSettingsResolver,
} from "@/souhl_admin/utils/form/smsSendingStartSettings"
import { formatJstDateUntilDayByHyphen } from "@/utils/date"

const smsSendingStartSettingsFragment = graphql(/* GraphQL */ `
	fragment smsSendingStartSettings on Salon {
		id
		...smsSendingStartSettingsTable
	}
`)

const updateSalonSmsSendingStartSettingsMutation = graphql(/* GraphQL */ `
	mutation updateSalonSmsSendingStartSettings(
		$input: UpdateSalonSmsSendingStartSettingsInput!
	) {
		updateSalonSmsSendingStartSettings(input: $input) {
			salonIds
		}
	}
`)

type Props = {
	companyId: string
	fragment: FragmentType<typeof smsSendingStartSettingsFragment>[]
	onUpdated: () => Promise<unknown>
}

const SmsSendingStartSettingsTab: React.FC<Props> = ({
	companyId,
	fragment,
	onUpdated,
}) => {
	const salons = getFragmentData(smsSendingStartSettingsFragment, fragment)

	const [selected, setSelected] = useState<Record<number, boolean>>({})

	// react-hook-form for bulk apply fields
	const methods = useForm<SmsSendingStartSettingsForm>({
		resolver: smsSendingStartSettingsResolver,
		defaultValues: {
			autoClaimBase: "RESERVATION_RECEIPTED_DATE",
			enableAutoClaimStartDate: true,
			enableEncourageStartCancelDate: true,
			autoClaimStartDate: formatJstDateUntilDayByHyphen(new Date()),
			encourageStartCancelDate: formatJstDateUntilDayByHyphen(new Date()),
		},
	})
	const { handleSubmit, formState, watch } = methods

	const [mutate, { loading: saving }] = useSouhlAdminMutation(
		updateSalonSmsSendingStartSettingsMutation
	)

	const onSubmit = async (values: SmsSendingStartSettingsForm) => {
		const salonIds = Object.entries(selected)
			.filter(([, v]) => v)
			.map(([k]) => Number(k))
		if (salonIds.length === 0) {
			alert("適用対象の店舗を選択してください")
			return
		}

		await mutate({
			variables: {
				input: {
					companyId,
					salonIds,
					autoClaimBase:
						values.autoClaimBase as CompanySmsSendingRuleAutoClaimBase,
					autoClaimStartDate: values.enableAutoClaimStartDate
						? (values.autoClaimStartDate ?? null)
						: null,
					encourageStartCancelDate: values.enableEncourageStartCancelDate
						? (values.encourageStartCancelDate ?? null)
						: null,
				},
			},
		})
		await onUpdated()
		alert("更新しました")
	}

	const selectedCount = useMemo(
		() => Object.values(selected).filter(Boolean).length,
		[selected]
	)

	return (
		<FormProvider {...methods}>
			<form className={styles.section} onSubmit={handleSubmit(onSubmit)}>
				<BulkApplyForm
					register={methods.register}
					watch={watch}
					errors={formState.errors}
					rightControls={
						selectedCount > 0 && (
							<>
								<button
									type="submit"
									className={styles.save_button}
									disabled={saving}
								>
									保存
								</button>
								<span>選択中: {selectedCount}件</span>
							</>
						)
					}
				/>

				<SalonSettingsTable
					fragment={salons}
					selected={selected}
					onToggle={(salonId, checked) =>
						setSelected((prev) => ({ ...prev, [salonId]: checked }))
					}
					onToggleAll={(checked) => {
						const next: Record<number, boolean> = {}
						salons.forEach((s) => (next[s.id] = checked))
						setSelected(next)
					}}
				/>
			</form>
		</FormProvider>
	)
}

export default SmsSendingStartSettingsTab
